import React, { createContext, useState, ReactNode, useContext, useRef, useEffect, useMemo } from 'react';
import LoaderWithPortal from '../components/LoaderWithPortal/LoaderWithPortal';
import { UserContext } from './UserContext';

interface AppLoadingFlags {
  initValidator: boolean;
  servicesInitializer: boolean;
}

interface AppLoadingContextType {
  loading: AppLoadingFlags;
  setLoading: React.Dispatch<React.SetStateAction<AppLoadingFlags>>;
}

interface Props {
  children: ReactNode;
}

export const AppLoadingContext = createContext<AppLoadingContextType>({
  loading: {
    initValidator: false,
    servicesInitializer: false
  },
  setLoading: () => {}
});

export const AppLoadingProvider = ({ children }: Props) => {
  const [loading, setLoading] = useState({
    initValidator: false,
    servicesInitializer: false
  });
  const data = useContext(UserContext);
  const userIsInitializedFromContext = data?.userIsInitialized;
  
  // Cache the loader root element to avoid repeated DOM queries
  const documentLoaderRootRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    // Only query DOM on client side and cache the result
    if (typeof window !== 'undefined') {
      documentLoaderRootRef.current = document.getElementById("core-root");
    }
  }, []);

  const isLoading = (!userIsInitializedFromContext || loading.initValidator || loading.servicesInitializer) && documentLoaderRootRef.current;
  
  // Memoize the context value to prevent unnecessary re-renders of consumer components
  const contextValue = useMemo(() => ({
    loading,
    setLoading
  }), [loading]);
  
  return (
    <AppLoadingContext.Provider value={contextValue}>
        {children}
        {isLoading ? <LoaderWithPortal htmlElement={documentLoaderRootRef.current} /> : null}
    </AppLoadingContext.Provider>
  );
};